import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { apagar, atualizarJson, editarJson, enviarJson, enviarSemConteudo, pedirJson } from "./api";

/* Canais de entrada: caixas de e-mail, domínios das organizações, quarentena, formulários públicos
   e distribuição por equipe. O contrato é o de `api/app/dominios/canais/` e
   `api/app/dominios/tickets/distribuicao_router.py`.

   O token de uma caixa de webhook e o de um formulário público só existem na resposta que os cria
   (ou gira): esta camada os devolve à tela uma vez e nunca os guarda no cache das listagens. */

const uuid = z.string().uuid();
const CHAVE = ["canais"] as const;

const esquemaCaixa = z.object({
  id: uuid,
  nome: z.string(),
  endereco: z.string(),
  adaptador: z.enum(["imap", "webhook"]),
  ativa: z.boolean(),
  autenticacao: z.enum(["exigir", "confiar"]),
  authserv_id: z.string().nullable(),
  equipe_id: uuid.nullable(),
  categoria: z.string().nullable(),
  imap_host: z.string().nullable(),
  imap_porta: z.number().int().nullable(),
  imap_usuario: z.string().nullable(),
  imap_senha_variavel: z.string().nullable(),
  imap_pasta: z.string(),
  imap_ssl: z.boolean(),
  token_prefixo: z.string().nullable(),
  ultimo_ciclo_em: z.string().nullable(),
  ultimo_erro: z.string().nullable(),
  criado_em: z.string(),
  recebidos_24h: z.number().int(),
});
const esquemaCaixaCriada = esquemaCaixa.extend({ token: z.string().nullable() });

const esquemaDominio = z.object({
  id: uuid,
  organizacao_id: uuid,
  dominio: z.string(),
  criado_em: z.string(),
});

const esquemaEmailRecebido = z.object({
  id: uuid,
  caixa_id: uuid,
  remetente: z.string().nullable(),
  assunto: z.string().nullable(),
  resultado: z.enum(["acao", "chamado", "quarentena", "ignorado"]),
  motivo: z.string().nullable(),
  organizacao_id: uuid.nullable(),
  ticket_id: uuid.nullable(),
  numero_do_ticket: z.number().int().nullable(),
  decisao: z.enum(["liberada", "descartada"]).nullable(),
  decidido_em: z.string().nullable(),
  recebido_em: z.string(),
  corpo_texto: z.string().nullable(),
});
const esquemaLiberacao = z.object({
  ticket_id: uuid,
  numero: z.number().int(),
  abriu_chamado: z.boolean(),
});

const esquemaFormulario = z.object({
  id: uuid,
  organizacao_id: uuid,
  nome: z.string(),
  token_prefixo: z.string(),
  categoria: z.string().nullable(),
  equipe_id: uuid.nullable(),
  ativo: z.boolean(),
  criado_em: z.string(),
  revogado_em: z.string().nullable(),
});
const esquemaFormularioCriado = esquemaFormulario.extend({ token: z.string() });

const esquemaFormularioPublico = z.object({ nome: z.string(), organizacao: z.string() });
const esquemaChamadoPublico = z.object({ numero: z.number().int().nullable(), mensagem: z.string() });

const esquemaMembro = z.object({
  usuario_id: uuid,
  nome: z.string(),
  email: z.string(),
  peso: z.number().int(),
  ativo: z.boolean(),
  carga_aberta: z.number().int(),
});
const esquemaDistribuicao = z.object({
  equipe_id: uuid,
  automatica: z.boolean(),
  membros: z.array(esquemaMembro),
});

const esquemaEquipe = z.object({ id: uuid, nome: z.string() });
const esquemaAgente = z.object({ id: uuid, nome: z.string(), email: z.string() });
const esquemaOrganizacao = z.object({
  id: uuid,
  razao_social: z.string(),
  nome_fantasia: z.string().nullable(),
});

export type CaixaDeEntrada = z.infer<typeof esquemaCaixa>;
export type CaixaCriada = z.infer<typeof esquemaCaixaCriada>;
export type DominioDaOrganizacao = z.infer<typeof esquemaDominio>;
export type EmailRecebido = z.infer<typeof esquemaEmailRecebido>;
export type FormularioPublico = z.infer<typeof esquemaFormulario>;
export type FormularioCriado = z.infer<typeof esquemaFormularioCriado>;
export type FormularioPublicoVisivel = z.infer<typeof esquemaFormularioPublico>;
export type Distribuicao = z.infer<typeof esquemaDistribuicao>;
export type MembroDaDistribuicao = z.infer<typeof esquemaMembro>;
export type EquipeDeAtendimento = z.infer<typeof esquemaEquipe>;
export type AgenteInterno = z.infer<typeof esquemaAgente>;
export type OrganizacaoDaLista = z.infer<typeof esquemaOrganizacao>;

/* ------------------------------------------------------------------ vocabulário */

export const ROTULO_DO_RESULTADO: Readonly<Record<EmailRecebido["resultado"], string>> = {
  chamado: "Abriu um chamado",
  acao: "Entrou na conversa de um chamado",
  quarentena: "Em quarentena",
  ignorado: "Ignorado",
};

export const ROTULO_DA_DECISAO: Readonly<Record<NonNullable<EmailRecebido["decisao"]>, string>> = {
  liberada: "Liberado pela equipe",
  descartada: "Descartado pela equipe",
};

export const ROTULO_DO_ADAPTADOR: Readonly<Record<CaixaDeEntrada["adaptador"], string>> = {
  imap: "Leitura de uma caixa IMAP",
  webhook: "Recebimento por webhook",
};

/** O nome de variável de ambiente que a API aceita para a senha do IMAP (`caixas.py`). */
export const PREFIXO_DA_VARIAVEL_DE_SENHA = "PORTAL_IMAP_";
const FORMATO_DA_VARIAVEL = /^PORTAL_IMAP_[A-Z0-9_]{1,60}$/;

export function variavelDeSenhaValida(valor: string): boolean {
  return FORMATO_DA_VARIAVEL.test(valor.trim());
}

/** O endereço de envio do e-mail cru da caixa de webhook, como o provedor precisa configurá-lo. */
export function enderecoDoWebhookDeEntrada(origem: string): string {
  return `${origem.replace(/\/$/, "")}/api/canais/entrada`;
}

/** O link público do formulário, montado a partir do token recebido na criação. */
export function linkDoFormulario(origem: string, token: string): string {
  return `${origem.replace(/\/$/, "")}/abrir/${encodeURIComponent(token)}`;
}

/** Quanto pesa o que a pessoa escolheu: "2× a carga" lê melhor que "peso 2" para o administrador. */
export function rotuloDoPeso(peso: number): string {
  return peso === 1 ? "Peso 1 (padrão)" : `Peso ${peso}`;
}

/* ------------------------------------------------------ formulário público (validação) */

export const esquemaDoPedidoPublico = z.object({
  nome: z.string().trim().min(2, "Informe o seu nome."),
  email: z
    .string()
    .trim()
    .regex(/^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/, "Informe um e-mail válido, como nome@empresa.com.br."),
  assunto: z.string().trim().min(3, "Conte o assunto em poucas palavras."),
  descricao: z.string().trim().min(5, "Descreva o que está acontecendo."),
});
export type PedidoPublico = z.infer<typeof esquemaDoPedidoPublico>;

/* ------------------------------------------------------------------------- hooks */

export function useCaixas(enabled = true) {
  return useQuery({
    queryKey: [...CHAVE, "caixas"],
    queryFn: ({ signal }) => pedirJson("/canais/caixas", z.array(esquemaCaixa), { signal }),
    enabled,
  });
}

export function useCriarCaixa() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (corpo: Record<string, unknown>) =>
      enviarJson("/canais/caixas", esquemaCaixaCriada, corpo),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: [...CHAVE, "caixas"] }),
  });
}

export function useAlterarCaixa() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({ id, corpo }: { id: string; corpo: Record<string, unknown> }) =>
      editarJson(`/canais/caixas/${encodeURIComponent(id)}`, esquemaCaixa, corpo),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: [...CHAVE, "caixas"] }),
  });
}

export function useGirarTokenDaCaixa() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      enviarJson(`/canais/caixas/${encodeURIComponent(id)}/token`, esquemaCaixaCriada, {}),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: [...CHAVE, "caixas"] }),
  });
}

export function useDominios(enabled = true) {
  return useQuery({
    queryKey: [...CHAVE, "dominios"],
    queryFn: ({ signal }) => pedirJson("/canais/dominios", z.array(esquemaDominio), { signal }),
    enabled,
  });
}

export function useCadastrarDominio() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (corpo: { organizacao_id: string; dominio: string }) =>
      enviarJson("/canais/dominios", esquemaDominio, corpo),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: [...CHAVE, "dominios"] }),
  });
}

export function useRemoverDominio() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apagar(`/canais/dominios/${encodeURIComponent(id)}`),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: [...CHAVE, "dominios"] }),
  });
}

export function useQuarentena(enabled = true) {
  return useQuery({
    queryKey: [...CHAVE, "quarentena"],
    queryFn: ({ signal }) => pedirJson("/canais/quarentena", z.array(esquemaEmailRecebido), { signal }),
    enabled,
  });
}

export function useEmailsRecebidos(resultado: EmailRecebido["resultado"] | "", enabled = true) {
  return useQuery({
    queryKey: [...CHAVE, "emails", resultado],
    queryFn: ({ signal }) =>
      pedirJson(
        `/canais/emails${resultado ? `?resultado=${resultado}` : ""}`,
        z.array(esquemaEmailRecebido),
        { signal },
      ),
    enabled,
  });
}

export function useLiberarDaQuarentena() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      corpo,
    }: {
      id: string;
      corpo: { organizacao_id?: string; ticket_id?: string };
    }) => enviarJson(`/canais/quarentena/${encodeURIComponent(id)}/liberar`, esquemaLiberacao, corpo),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: [...CHAVE, "quarentena"] });
      void cliente.invalidateQueries({ queryKey: [...CHAVE, "emails"] });
      void cliente.invalidateQueries({ queryKey: ["tickets"] });
    },
  });
}

export function useDescartarDaQuarentena() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      enviarSemConteudo(`/canais/quarentena/${encodeURIComponent(id)}/descartar`),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: [...CHAVE, "quarentena"] });
      void cliente.invalidateQueries({ queryKey: [...CHAVE, "emails"] });
    },
  });
}

export function useFormularios(enabled = true) {
  return useQuery({
    queryKey: [...CHAVE, "formularios"],
    queryFn: ({ signal }) =>
      pedirJson("/canais/formularios", z.array(esquemaFormulario), { signal }),
    enabled,
  });
}

export function useCriarFormulario() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (corpo: {
      organizacao_id: string;
      nome: string;
      categoria?: string | null;
      equipe_id?: string | null;
    }) => enviarJson("/canais/formularios", esquemaFormularioCriado, corpo),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: [...CHAVE, "formularios"] }),
  });
}

export function useRevogarFormulario() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      enviarJson(`/canais/formularios/${encodeURIComponent(id)}/revogar`, esquemaFormulario, {}),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: [...CHAVE, "formularios"] }),
  });
}

export function useGirarTokenDoFormulario() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      enviarJson(`/canais/formularios/${encodeURIComponent(id)}/token`, esquemaFormularioCriado, {}),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: [...CHAVE, "formularios"] }),
  });
}

export function useOrganizacoesParaCanais(enabled = true) {
  return useQuery({
    queryKey: [...CHAVE, "organizacoes"],
    queryFn: ({ signal }) =>
      pedirJson("/organizacoes?limite=200", z.array(esquemaOrganizacao), { signal }),
    enabled,
  });
}

export function useEquipesParaCanais(enabled = true) {
  return useQuery({
    queryKey: [...CHAVE, "equipes"],
    queryFn: ({ signal }) => pedirJson("/tickets/equipes", z.array(esquemaEquipe), { signal }),
    enabled,
  });
}

export function useAgentesParaCanais(enabled = true) {
  return useQuery({
    queryKey: [...CHAVE, "agentes"],
    queryFn: ({ signal }) => pedirJson("/tickets/agentes", z.array(esquemaAgente), { signal }),
    enabled,
  });
}

export function useDistribuicao(equipeId: string, enabled = true) {
  return useQuery({
    queryKey: [...CHAVE, "distribuicao", equipeId],
    queryFn: ({ signal }) =>
      pedirJson(`/tickets/equipes/${encodeURIComponent(equipeId)}/distribuicao`, esquemaDistribuicao, {
        signal,
      }),
    enabled: enabled && Boolean(equipeId),
  });
}

export function useDefinirDistribuicao(equipeId: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (corpo: {
      automatica: boolean;
      membros: { usuario_id: string; peso: number; ativo: boolean }[];
    }) =>
      atualizarJson(
        `/tickets/equipes/${encodeURIComponent(equipeId)}/distribuicao`,
        esquemaDistribuicao,
        corpo,
      ),
    onSuccess: () =>
      void cliente.invalidateQueries({ queryKey: [...CHAVE, "distribuicao", equipeId] }),
  });
}

/* Formulário público: sem sessão, o que a API devolve é o mínimo. */

export function useFormularioPublico(token: string) {
  return useQuery({
    queryKey: [...CHAVE, "publico", token],
    queryFn: ({ signal }) =>
      pedirJson(
        `/publico/formularios/${encodeURIComponent(token)}`,
        esquemaFormularioPublico,
        { signal },
      ),
    retry: false,
    enabled: Boolean(token),
  });
}

export function useAbrirChamadoPublico(token: string) {
  return useMutation({
    mutationFn: (corpo: PedidoPublico & { site: string }) =>
      enviarJson(
        `/publico/formularios/${encodeURIComponent(token)}/chamados`,
        esquemaChamadoPublico,
        corpo,
      ),
  });
}
